import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DecorLook, EventDecor, FloorPlan } from '@peles/shared';
import * as api from '../api/decor';

export const eventDecorKey = (eventId: string) => ['events', eventId, 'decor'] as const;

/** The event's look and its pictures' colours. */
export function useEventDecor(eventId: string | null | undefined) {
  return useQuery<EventDecor>({
    queryKey: eventDecorKey(eventId ?? ''),
    queryFn: () => api.getEventDecor(eventId!),
    enabled: !!eventId,
  });
}

/** Saves the event's look; the answer is what the server stored. */
export function useSaveEventDecor(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (decor: EventDecor) => api.saveEventDecor(eventId, decor),
    onSuccess: (stored) => queryClient.setQueryData(eventDecorKey(eventId), stored),
  });
}

/**
 * Saves a plan's changes to the look. The plan's cached copy takes the new
 * look without being fetched again — a refetch of the plan open in the editor
 * is not this change's to cause.
 */
export function useSaveFloorPlanDecor(floorPlanId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (look: DecorLook | null) => api.saveFloorPlanDecor(floorPlanId, look),
    onSuccess: (decor) =>
      queryClient.setQueryData<FloorPlan>(['floor-plans', floorPlanId], (plan) =>
        plan ? { ...plan, decor } : plan,
      ),
  });
}

/** Asks the AI what the pictures show. */
export function useSuggestDecor(eventId: string) {
  return useMutation({ mutationFn: () => api.suggestEventDecor(eventId) });
}
